import { AlertTriangle } from 'lucide-react'
import React from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Typogaphy } from 'vnetwork-ui'

import SubHeaders from '@/components/Headers/SubHeaders'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'

import { useBusinessCreate } from '../../hooks/use-business-create'
import Statistical from './Statistical'
import { BusinessCreateForm } from './business-create-form'

/**
 * Business Create Page
 * Creates new business trip requests
 */
const Create: React.FC<IPermissionProps> = () => {
    const {
        // Form
        control,
        handleSubmit,
        isPending,
        selectedBusinessType,
        selectedMeetingPurpose,
        // Customer
        customerIdSearch,
        handleNoOptionCustomer,
        // Time entries
        timeEntries,
        setTimeEntries,
        handleAddDay,
        minDate,
        // Validation
        dateValidation,
        journeyId,
        // Submit
        onSubmit,
        t,
    } = useBusinessCreate()

    return (
        <div>
            {/* Header */}
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.businessAccountList,
                        title: t('business-list'),
                    },
                    {
                        path: paths.businessAccountCreate,
                        title: t('business-create'),
                    },
                ]}
            />

            {/* Statistics */}
            <Statistical />

            {/* Policy Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mt-2">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <Typogaphy className="font-medium text-amber-800">
                            {t('policy_notice_title')}
                        </Typogaphy>
                        <Typogaphy size="small" className="text-amber-700 mt-1">
                            {t('policy_notice_content')}
                        </Typogaphy>
                    </div>
                </div>
            </div>

            {/* Date Validation Error */}
            {!dateValidation.isValid && (
                <Alert
                    variant="error"
                    className="mt-2 border border-red-200 rounded-sm"
                >
                    {dateValidation.error}
                </Alert>
            )}

            {/* Form */}
            <div className="bg-white mt-2 rounded-sm border border-gray-200">
                <form>
                    <Typogaphy
                        size="medium"
                        className="p-4 text-primary font-medium"
                    >
                        {t('business-detail')}
                    </Typogaphy>

                    <BusinessCreateForm
                        control={control}
                        t={t}
                        selectedBusinessType={selectedBusinessType}
                        selectedMeetingPurpose={selectedMeetingPurpose}
                        customerIdSearch={customerIdSearch}
                        journeyId={journeyId}
                        handleNoOptionCustomer={handleNoOptionCustomer}
                        timeEntries={timeEntries}
                        setTimeEntries={setTimeEntries}
                        handleAddDay={handleAddDay}
                        minDate={minDate}
                    />
                </form>

                {/* Actions */}
                <div className="p-4 flex items-center justify-end gap-4 bg-white rounded-[8px] mt-2">
                    <Link to="/account/businesses">
                        <Button variant="outlined" size="small">
                            {t('cancle-operation')}
                        </Button>
                    </Link>
                    <Button
                        id="business_create"
                        variant="contained"
                        size="small"
                        loading={isPending}
                        disabled={isPending || !dateValidation.isValid}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {t('business-create')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default Create
